import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./CustomAudioPlayer.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import {
  faPlay,
  faPause,
  faVolumeUp,
  faVolumeMute,
} from "@fortawesome/free-solid-svg-icons";

const CustomAudioPlayer = ({ src, title }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // UUSI
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleCanPlay = () => setIsLoading(false); // Kun valmis soittamaan

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("canplaythrough", handleCanPlay);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("canplaythrough", handleCanPlay);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      setIsLoading(true); // Aseta lataustila ennen toistoa
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Toisto epäonnistui:", err);
          setIsLoading(false);
        });
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handlePlaybackRateChange = () => {
    const speeds = [0.75, 1, 1.25, 1.5, 2];
    const idx = speeds.indexOf(playbackRate);
    const next = speeds[(idx + 1) % speeds.length];
    if (audioRef.current) audioRef.current.playbackRate = next;
    setPlaybackRate(next);
  };

  const handleVolumeChange = e => {
    const v = parseFloat(e.target.value);
    if (audioRef.current) audioRef.current.volume = v;
    setVolume(v);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !audioRef.current.muted;
    setVolume(audioRef.current.muted ? 0 : audioRef.current.volume);
  };

  const handleSeek = e => {
    if (!audioRef.current || duration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const t = pct * duration;
    audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const formatTime = t => {
    const m = Math.floor(t / 60).toString().padStart(2, "0");
    const s = Math.floor(t % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  if (!src) return null;

  return (
    <div className="custom-audio-player">
      <div className="track-title">{title || ""}</div>
      <audio ref={audioRef} src={src} preload="none" />
      <div className="timeline" onClick={handleSeek}>
        <div
          className="timeline-progress"
          style={{ width: `${(currentTime / duration) * 100}%` }}
        />
      </div>
      <div className="time-info">
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>

      {isLoading && (
        <div className="text-sm text-joyridePurple dark:text-joyrideNavy mb-2">
          🎧 Ladataan ääntä...
        </div>
      )}

      <div className="controls">
        <motion.button onClick={togglePlayPause} whileTap={{ scale: 0.9 }}>
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
        </motion.button>
        <motion.button onClick={handlePlaybackRateChange} whileTap={{ scale: 0.9 }}>
          {playbackRate}x
        </motion.button>
        <motion.button onClick={toggleMute} whileTap={{ scale: 0.9 }}>
          <FontAwesomeIcon icon={volume === 0 ? faVolumeMute : faVolumeUp} />
        </motion.button>
        <input
          type="range"
          min="0" max="1" step="0.01"
          value={volume}
          onChange={handleVolumeChange}
        />
      </div>
    </div>
  );
};

CustomAudioPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
};

export default CustomAudioPlayer;
